import { useEffect, useRef, useState } from "react";
import {
  TextField,
  List,
  ListItemButton,
  Paper,
  Box,
  Typography,
  Portal,
} from "@mui/material";
import axios from "axios";

const FloridaAddressAutocomplete = ({
  label = "Address",
  onSelect,
  address,
  inputValue,
  setInputValue,
  currentSession,
}) => {
  const [suggestions, setSuggestions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [isSelectingOption, setIsSelectingOption] = useState(false);
  const [dropdownPosition, setDropdownPosition] = useState({
    top: 0,
    left: 0,
    width: 0,
  });
  const containerRef = useRef(null);
  const debounceTimerRef = useRef(null);

  // Update dropdown position
  const updateDropdownPosition = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setDropdownPosition({
        top: rect.bottom + window.scrollY,
        left: rect.left + window.scrollX,
        width: rect.width,
      });
    }
  };

  const fetchSuggestions = async (value) => {
    if (!value.trim() || value.length < 3) {
      setSuggestions([]);
      return;
    }

    try {
      setLoading(true);
      const { data } = await axios.post(
        `${process.env.REACT_APP_BASE_URL}/nocApi/real-estate/get-county-and-city`,
        {
          address: value,
          currentSession: currentSession,
        },
        { withCredentials: true }
      );

      if (data.success && data.data && data.data.length > 0) {
        setSuggestions(data.data);
        updateDropdownPosition();
      } else {
        setSuggestions([]);
      }
    } catch (error) {
      console.error("Error fetching address suggestions:", error);
      setSuggestions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const value = e.target.value;
    setInputValue(value);

    // If user completely clears the input, clear the parent's address state
    if (value === "") {
      onSelect({}); // Clear the address object
      setSuggestions([]);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      return;
    }

    // If typing but input is too short, just clear suggestions (keep address)
    if (value.trim().length < 3) {
      setSuggestions([]);
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      return;
    }

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    // Extract just the street address part for searching (before first comma if present)
    const searchValue = value.split(",")[0].trim();

    // Debounce API calls (wait 300ms after user stops typing)
    debounceTimerRef.current = setTimeout(() => {
      fetchSuggestions(searchValue);
    }, 300);
  };

  const handleSelect = (result) => {
    setIsSelectingOption(true);
    // Use the full formatted address with city and county
    const fullAddressWithLocation =
      result.address?.full_with_city_county ||
      result.address?.full_address ||
      "";
    setInputValue(fullAddressWithLocation);
    setSuggestions([]);
    onSelect(result); // Pass the full result object, replacing any previous selection

    // Reset flag after a short delay
    setTimeout(() => {
      setIsSelectingOption(false);
    }, 100);
  };

  // Initialize inputValue from address prop
  useEffect(() => {
    if (address) {
      const addressString =
        typeof address === "string"
          ? address
          : address?.address?.full_with_city_county ||
            address?.address?.full_address ||
            "";
      if (addressString && !inputValue) {
        setInputValue(addressString);
      }
    }
  }, [address, inputValue, setInputValue]);

  // Cleanup debounce timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <Box sx={{ position: "relative", overflow: "visible" }} ref={containerRef}>
      <TextField
        label={label}
        fullWidth
        variant="filled"
        value={inputValue || ""}
        onChange={handleChange}
        onFocus={updateDropdownPosition}
        onBlur={() => {
          // Don't interfere if user is clicking an option
          if (isSelectingOption) {
            return;
          }

          // Restore the selected address if one exists, unless input was fully cleared
          if (address?.address?.full_with_city_county) {
            const addressString =
              typeof address === "string"
                ? address
                : address?.address?.full_with_city_county ||
                  address?.address?.full_address ||
                  "";
            setInputValue(addressString);
          }

          setTimeout(() => setSuggestions([]), 200);
        }}
        helperText={loading ? "Searching Florida properties..." : ""}
      />
      {suggestions.length > 0 && (
        <Portal>
          <Paper
            onMouseDown={(e) => {
              // Prevent blur from firing when clicking options
              e.preventDefault();
            }}
            sx={{
              position: "fixed",
              zIndex: 9999,
              left: `${dropdownPosition.left}px`,
              top: `${dropdownPosition.top + 4}px`,
              width: `${dropdownPosition.width}px`,
              maxHeight: 200,
              overflowY: "auto",
              boxShadow: 3,
            }}
          >
            <List disablePadding>
              {suggestions.map((result, idx) => (
                <ListItemButton
                  key={`${result.parcel_id}-${idx}`}
                  onClick={() => handleSelect(result)}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "flex-start",
                    py: 1.5,
                    px: 2,
                    "&:hover": {
                      backgroundColor: "action.hover",
                    },
                  }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {[
                      result.address?.full_address,
                      result.city && result.city.trim() ? result.city : null,
                      result.county?.title,
                    ]
                      .filter(Boolean)
                      .join(", ")}
                  </Typography>
                </ListItemButton>
              ))}
            </List>
          </Paper>
        </Portal>
      )}
    </Box>
  );
};

export default FloridaAddressAutocomplete;
